import JSEncrypt from 'jsencrypt';
import { PUBLIC_KEY, PRIVATE_KEY } from '@/utils/config';
import { get_bot_key } from '@/apis/common';
import CryptoJS from 'crypto-js';
import { getGlobalStore } from '@/store/global';

// 生成uuid
export const getUuid = () => {
    let d = new Date().getTime();
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        d += performance.now();
    }
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
    return uuid;
};

// 公钥加密
const getEncryption = (data: string) => {
    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(PUBLIC_KEY);
    const encryptedMessage = encrypt.encrypt(data);
    return encryptedMessage as string;
};

// 私钥解密
const getDecryption = (data: string) => {
    const encrypt = new JSEncrypt();
    encrypt.setPrivateKey(PRIVATE_KEY);
    const encryptedMessage = encrypt.decrypt(data);
    return encryptedMessage as string;
};

// 非对称加密方式，获取加密botKey
export const getBotKey = (botId: string): Promise<string> =>
    new Promise(async resolve => {
        try {
            if (!botId) {
                resolve('');
                return;
            }
            const encryptedBotId = getEncryption(botId);
            if (!encryptedBotId) {
                resolve('');
                return;
            }
            const { status, result } = await get_bot_key({ encryptedBotId });
            if (status === 0 && result) {
                resolve(getDecryption(result));
            } else {
                resolve('');
            }
        } catch (error) {
            resolve('');
        }
    });

// 获取签名
export const getSign = async (params: { botKey: string; timestamp: number; uuid: string }) => {
    const { botKey, timestamp, uuid } = params;
    const textToSign = timestamp + uuid;
    const hash = CryptoJS.HmacSHA256(textToSign, botKey);
    return hash.toString(CryptoJS.enc.Base64);
};

export type TMessageType =
    | 'text'
    | 'function_call'
    | 'tool_calls'
    | 'incomplete_answer'
    | 'retry'
    | 'sensitive_words_replace'
    | 'toast_error'
    | 'stream_error'
    | 'token_fail'
    | 'modal_error'
    | 'no_ask_again_num'
    | 'recharge';

interface IMessageData {
    type: TMessageType;
    content?: string;
}

export interface IToolCall {
    id: string;
    name: string;
    args: string | Record<string, string>;
}

export interface IFunctionCall {
    name: string;
    args: string | Record<string, string>;
}

interface IFormatMessage extends IMessageData {
    functionCall?: IFunctionCall;
    toolCalls?: IToolCall[];
    /**
     * 弱网情况下，流返回的字符串数据会被截断，导致用JSON.parse方式转换为对象时会报错，
     * 需要将导致报错的一段流字符保留下来，和下一段流字符拼接继续后续的流解析
     */
    preStreamMessage?: string;
}

interface IOptions {
    preAnswerContent: string;
    preFunctionCall?: IFunctionCall;
    preToolCalls: IToolCall[];
}

export const statusToErrorInfo = (status: number, message: string): IMessageData => {
    switch (status) {
        /*  敏感词 */
        case 10513302:
        case 66661064:
            return { type: 'toast_error', content: '您的提问涉嫌违规，请修改内容后重新提问' };
        case 66661055:
            return { type: 'toast_error', content: 'Luca正在回复，请稍后发送' };
        /* 双端操作状态不对报错 */
        case 10513472:
            return { type: 'toast_error', content: message };
        /* 每天创建问题数超过限制 */
        case 10513466:
            return { type: 'modal_error', content: message };
        /*  bot记录不存在 / prompt不存在 / luca已停用 / prompt数据被删 / 服务不可用 */
        // case 66661002:
        // case 66661006:
        // case 10513004:
        // case 66661066:
        // case 10513006:
        // case -1:
        //     return { type: 'stream_error', content: '很抱歉，服务好像出了点问题，请点击下方【未解决】按钮，将问题提交给名师回答' };
        case 66661063:
            return { type: 'stream_error', content: message || '很抱歉，这个问题有点难，我暂时无法回答' };
        case 10513469:
            return {
                type: 'stream_error',
                content:
                    message ||
                    '我暂时无法回答关于图片的问题，请用文字描述，可快速获得解答。或者点击下方【未解决】按钮，将问题提交给名师回答，但需等待一段时间哦~',
            };
        case 10513467:
            return {
                type: 'no_ask_again_num',
                content: message || '你的追问次数已用完，若还未解决你的疑问，请点击下方【未解决】按钮，将问题提交给名师回答',
            };
        case 553649434:
        case 553649443:
        case 553649437:
            return { type: 'token_fail' };
        /*  没有次数，需要充值  */
        case 10513475:
            return { type: 'recharge' };
        default:
            /*  重试 */
            return { type: 'retry' };
    }
};

// 消息流处理
export const transformCharToMessage = (message: string, { preAnswerContent, preToolCalls, preFunctionCall }: IOptions): IFormatMessage => {
    const returnMsg: IFormatMessage = {
        type: 'text',
        content: preAnswerContent,
        functionCall: preFunctionCall,
        toolCalls: preToolCalls,
    };
    let preStreamMessage = message;
    try {
        const splitMessage: string[] = message.split('\n\n');
        for (let i = 0; i < splitMessage.length; i++) {
            if (splitMessage[i] === '') continue;
            if (i !== 0) preStreamMessage = splitMessage[i];
            const jsonStrMessage = splitMessage[i].replace(/^data:/g, '');
            const jsonMessage = JSON.parse(jsonStrMessage);
            const { status = 0, message, error, bizCode, choices = [], answer = '' } = jsonMessage || {};
            if (status !== 0) return statusToErrorInfo(Number(status), message);
            /* gpt报错 */
            if (error) return { type: 'retry' };
            switch (bizCode) {
                /*  token超长 */
                case '196':
                    return { type: 'stream_error', content: message || '很抱歉，您发送的信息内容过长，请创建新对话或精简后重新发送。' };
                /*  敏感词 */
                case '197':
                    return { type: 'sensitive_words_replace', content: answer };
                default:
                    break;
            }
            const { finish_reason, delta = {} } = choices[0] || {};
            switch (finish_reason) {
                /*  未回答完整 */
                case 'length':
                    return { ...returnMsg, type: 'incomplete_answer' };
                /*  返回functio_call数据 */
                case 'function_call': {
                    const newFunctionCall = {
                        name: returnMsg.functionCall!.name,
                        args: JSON.parse(returnMsg.functionCall!.args as string),
                    };
                    return { ...returnMsg, type: 'function_call', functionCall: newFunctionCall };
                }
                case 'tool_calls':
                    return {
                        type: 'tool_calls',
                        toolCalls: preToolCalls.map(item => ({
                            ...item,
                            args: JSON.parse(item.args as string),
                        })),
                    };
                default:
                    break;
            }
            const { content = '', function_call, tool_calls } = delta;
            if (function_call) {
                returnMsg.type = 'function_call';
                returnMsg.functionCall = {
                    name: function_call.name || returnMsg.functionCall?.name,
                    args: (returnMsg.functionCall?.args || '') + function_call.arguments,
                };
            }
            if (tool_calls?.length) {
                returnMsg.type = 'tool_calls';
                returnMsg.toolCalls = tool_calls.reduce((total, item) => {
                    const { function: fn = {}, id, index } = item || {};
                    const { name, arguments: args } = fn || {};
                    if (id) {
                        total[index] = { name, id, args: args };
                    } else {
                        total[index].args += args;
                    }
                    return total;
                }, preToolCalls);
            }
            returnMsg.content += content;
        }
        return returnMsg;
    } catch (e) {
        returnMsg.preStreamMessage = preStreamMessage;
        return returnMsg;
    }
};

// 生成唯一的聊天记录的id
export const createQaRecordId = (role: 'questioner' | 'respondent' | 'system') => {
    return `${role}_record_key_${getUuid()}`;
};

// 流式执行回调
export const streamReturnContent = async (content: string, callback: (content: string) => void) => {
    if (!content) return;
    let index = 0;
    let currentContent = '';
    return new Promise(resolve => {
        const timer = setInterval(() => {
            if (index >= content.length) {
                clearInterval(timer);
                resolve('end');
            } else {
                currentContent += content[index];
                callback(currentContent);
            }
            index++;
        }, 50);
    });
};

export const toastInQa = (message: string, iconType: 'warning' | 'success' = 'warning') => {
    getGlobalStore()?.publish('Toast', { type: 'show', message, iconType });
};
